import create from "zustand";
import { configurePersist } from "zustand-persist";
import Fuse from "fuse.js/dist/fuse";

import {
  getAvailableCategoriesWithTags,
  getFilteredAndSearchedSolutions,
  getFilteredItems,
} from "../utilities/filterUtility";
import parseJsonFromDom from "../hooks/useJsonFromDomParser";

const overviewData = parseJsonFromDom("#nc-solutions__overview-data");
const allCategoriesWithTagsCacheHash = parseJsonFromDom(
  "#nc-solutions__overview-data-all-categories-with-tags-cache-hash"
);
const translations = parseJsonFromDom("#nc-solutions__overview-translations");

const { allSolutions, allCategoriesWithTags } = overviewData;

const { persist } = configurePersist({
  storage: sessionStorage, // use session storage because we don't want to restore the active filters in a new session
  rootKey: `ncSolution-${allCategoriesWithTagsCacheHash}`, // discriminate storage key by changes on the tags (to avoid possible removed filterTags from being active)
});

/**
 * initialize fuse search configuration
 */
const fuse = new Fuse(allSolutions, {
  keys: ["title", "overviewText"],
  threshold: 0.3,
});

export default create(
  persist(
    {
      key: "filterStore", // required, child key of storage
      allowlist: ["activeFilterTagUids", "currentSearchTerm"], // which properties we want to persist
    },
    (set, get) => ({
      currentPage: 0,
      translations,
      allSolutions,
      filteredAndSearchedSolutions: () =>
        getFilteredAndSearchedSolutions(
          get().searchedSolutions(),
          get().activeFilterTagUids
        ),
      allCategoriesWithTags,
      availableCategoriesWithTags: () =>
        getAvailableCategoriesWithTags(
          allCategoriesWithTags,
          get().filteredAndSearchedSolutions()
        ),
      activeFilterTagUids: [], // Note this could automatically be filled by the persist layer (from the session storage)
      setFilterState: (filterUid, isChecked) => {
        let activeFilterTagUids = get().activeFilterTagUids;

        if (isChecked) {
          activeFilterTagUids = [...activeFilterTagUids, filterUid];
        } else {
          activeFilterTagUids = activeFilterTagUids.filter(
            (id) => id !== filterUid
          );
        }

        const searchedSolutions = get().searchedSolutions();
        const categoriesWithTags = get().allCategoriesWithTags;
        set({
          ...getFilteredItems(
            searchedSolutions,
            activeFilterTagUids,
            categoriesWithTags
          ),
          activeFilterTagUids,
        });
      },
      setCurrentPage: (newCurrentPage) => {
        set({ currentPage: newCurrentPage });
      },

      searchedSolutions: () =>
        get().currentSearchTerm
          ? fuse.search(get().currentSearchTerm).map((result) => result.item)
          : get().allSolutions,
      currentSearchTerm: "",
      setCurrentSearchTerm: (searchTerm) => {
        const searchResults = fuse.search(searchTerm);
        const searchedSolutions = searchTerm
          ? searchResults.map((result) => result.item)
          : get().allSolutions;
        const categoriesWithTags = get().allCategoriesWithTags;
        const activeFilterTagUids = get().activeFilterTagUids;
        set({
          ...getFilteredItems(
            searchedSolutions,
            activeFilterTagUids,
            categoriesWithTags
          ),
          currentSearchTerm: searchTerm,
        });
      },
    })
  )
);
